import React, { useState, useEffect, useRef, useMemo } from 'react';

function useSyncExternalStoreShim(subscribe, getSnapshot, getServerSnapshot) {
  // eslint-disable-next-line @naverpay/typescript/prevent-default-import
  if ('useSyncExternalStore' in React && typeof React.useSyncExternalStore === 'function') {
    // eslint-disable-next-line @naverpay/typescript/prevent-default-import, react-hooks/rules-of-hooks
    return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot || getSnapshot);
  }
  const serverSnapshot = typeof window === 'undefined' ? getServerSnapshot?.() : undefined;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [state, setState] = useState(() => serverSnapshot || getSnapshot());

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const checkForUpdates = () => {
      const newState = getSnapshot();
      setState(prevState => prevState !== newState ? newState : prevState);
    };
    const unsubscribe = subscribe(checkForUpdates);
    checkForUpdates(); // 초기 값 설정

    return () => unsubscribe();
  }, [subscribe, getSnapshot]);
  return state;
}

/**
 * @see https://github.com/zloirock/core-js/blob/master/packages/core-js/internals/same-value.js
 */
const is = Object.is || function is(x, y) {
  // eslint-disable-next-line no-self-compare -- NaN check
  return x === y ? x !== 0 || 1 / x === 1 / y : x !== x && y !== y;
};
function useSyncExternalStoreWithSelectorShim(subscribe, getSnapshot, getServerSnapshot, selector) {
  let equalityFn = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : (a, b) => a === b;
  // 외부 값 변화 추적용
  const instantRef = useRef(null);
  let instant;
  if (instantRef.current === null) {
    instant = {
      hasValue: false,
      value: null
    };
    instantRef.current = instant;
  } else {
    instant = instantRef.current;
  }
  const [getSelection, getServerSelection] = useMemo(() => {
    let hasMemo = false;
    // 메모이제이션된 값 (클로저)
    // useRef로 두지 않은 이유 : 외부 state이므로 모든 동시성 렌더링 때 쓰여야 함
    // 클로저를 사용하면 각 동시성 인스턴스마다 고유의 상태를 가질 수 있으며, 함수가 생성될 때의 환경을 기억하므로 각 호출마다 독립된 환경을 유지
    // useRef는 다른 인스턴스에서 값을 변경하면 상태 공유 문제로 인해 다른 인스턴스에서도 그 변경이 반영되어 예상치 못한 문제를 야기할 수 있음
    let memoizedSnapshot;
    let memoizedSelection;
    const memoizedSelector = nextSnapshot => {
      if (!hasMemo) {
        hasMemo = true;
        memoizedSnapshot = nextSnapshot;
        const nextSelection = selector(nextSnapshot);
        if (equalityFn !== undefined) {
          if (instant.hasValue) {
            const currentSelection = instant.value;
            if (equalityFn(currentSelection, nextSelection)) {
              memoizedSelection = currentSelection;
              return currentSelection;
            }
          }
        }
        memoizedSelection = nextSelection;
        return nextSelection;
      }
      const prevSnapshot = memoizedSnapshot;
      const prevSelection = memoizedSelection;
      if (is(prevSnapshot, nextSnapshot)) {
        return prevSelection;
      }
      const nextSelection = selector(nextSnapshot);
      if (equalityFn !== undefined && equalityFn(prevSelection, nextSelection)) {
        memoizedSnapshot = nextSnapshot;
        return prevSelection;
      }
      memoizedSnapshot = nextSnapshot;
      memoizedSelection = nextSelection;
      return nextSelection;
    };
    const maybeGetServerSnapshot = getServerSnapshot === undefined ? null : getServerSnapshot;
    const getSnapshotWithSelector = () => memoizedSelector(getSnapshot());
    const getServerSnapshotWithSelector = maybeGetServerSnapshot === null ? undefined : () => memoizedSelector(maybeGetServerSnapshot());
    return [getSnapshotWithSelector, getServerSnapshotWithSelector];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSnapshot, getServerSnapshot, selector, equalityFn]);
  const value = useSyncExternalStoreShim(subscribe, getSelection, getServerSelection);
  useEffect(() => {
    instant.hasValue = true;
    instant.value = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return value;
}

export { useSyncExternalStoreShim, useSyncExternalStoreWithSelectorShim };
